import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Map,
  Bell,
  MessageSquare,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  Shield,
  Radio,
  ChevronRight,
} from 'lucide-react';
import { notificationsApi } from '../../lib/api';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin, isPersonnel } = useAuth();
  const { socket } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if user is Dispatch personnel (can access admin features)
  const isDispatch = user?.role === 'personnel' && user?.department === 'Dispatch';
  const canAccessAdmin = user?.role === 'admin' || isDispatch;

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await notificationsApi.getAll();
        const unread = response.data.filter(n => !n.is_read).length;
        setUnreadNotifications(unread);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    fetchNotifications();

    if (socket) {
      socket.on('new_notification', () => {
        setUnreadNotifications(prev => prev + 1);
      });
    }

    return () => {
      if (socket) {
        socket.off('new_notification');
      }
    };
  }, [socket]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Map, roles: ['public', 'personnel', 'admin'] },
    { path: '/notifications', label: 'Alerts', icon: Bell, roles: ['public', 'personnel', 'admin'], badge: unreadNotifications },
    { path: '/chat', label: 'Personnel Chat', icon: MessageSquare, roles: ['personnel', 'admin'] },
    { path: '/admin', label: 'Admin Panel', icon: Shield, roles: ['admin'], showForDispatch: true },
  ];

  const filteredNavItems = navItems.filter(item => {
    // Show for admin role
    if (item.roles.includes(user?.role)) return true;
    // Show Admin Panel for Dispatch personnel
    if (item.showForDispatch && isDispatch) return true;
    return false;
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'personnel': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-50 bg-zinc-900/95 backdrop-blur-xl border-r border-zinc-800 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <Radio className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-headings font-bold text-sm uppercase tracking-wide">Emergency</h1>
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Services</p>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-zinc-400 hover:text-white"
            data-testid="sidebar-toggle"
          >
            <ChevronRight className={`w-5 h-5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors relative ${
                    isActive
                      ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <>
                      <span className="font-medium">{item.label}</span>
                      {item.badge > 0 && (
                        <Badge className="ml-auto bg-red-500 text-white text-xs px-2">
                          {item.badge}
                        </Badge>
                      )}
                    </>
                  )}
                  {!sidebarOpen && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Profile */}
        <div className="p-4 border-t border-zinc-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full flex items-center gap-3 ${sidebarOpen ? 'justify-start' : 'justify-center'} h-auto py-3`}
                data-testid="user-menu-trigger"
              >
                <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" />
                </div>
                {sidebarOpen && (
                  <div className="text-left">
                    <p className="font-medium text-sm truncate">{user?.name}</p>
                    <Badge variant="outline" className={`text-[10px] mt-1 ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role?.toUpperCase()}
                    </Badge>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
              <DropdownMenuItem className="text-zinc-400">
                <User className="w-4 h-4 mr-2" />
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-400" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
            <Radio className="w-4 h-4 text-white" />
          </div>
          <span className="font-headings font-bold text-sm uppercase">Emergency Services</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-zinc-950/95 backdrop-blur-xl pt-16">
          <nav className="p-4 space-y-2">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-4 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.badge > 0 && (
                    <Badge className="ml-auto bg-red-500 text-white">{item.badge}</Badge>
                  )}
                </Link>
              );
            })}
            <div className="pt-4 border-t border-zinc-800 mt-4">
              <Button
                variant="ghost"
                className="w-full justify-start text-red-400 hover:text-red-300"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} pt-16 lg:pt-0`}>
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
